/**
 * Cookie / analytics consent (PIPEDA — meaningful consent for optional tracking).
 * Essential site features (checkout, promo session memory) do not require this choice.
 */

export type CookieConsentLevel = 'essential' | 'analytics';

const STORAGE_KEY = 'nannyb_cookie_consent';
const CONSENT_EVENT = 'nannyb-cookie-consent';

type Listener = (level: CookieConsentLevel) => void;
const listeners = new Set<Listener>();

/** True when GA4 or Meta Pixel env IDs are configured at build time. */
export function hasOptionalTrackingConfigured(): boolean {
  return Boolean(import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_FB_PIXEL_ID);
}

export function getCookieConsent(): CookieConsentLevel | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'essential' || raw === 'analytics') return raw;
    return null;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  return getCookieConsent() === 'analytics';
}

export function setCookieConsent(level: CookieConsentLevel): void {
  try {
    localStorage.setItem(STORAGE_KEY, level);
  } catch {
    /* private browsing */
  }
  listeners.forEach((fn) => fn(level));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: level }));
  }
}

/** Subscribe to consent changes (same tab + custom event). */
export function subscribeCookieConsent(listener: Listener): () => void {
  listeners.add(listener);
  const onEvent = (e: Event) => {
    const level = (e as CustomEvent<CookieConsentLevel>).detail;
    if (level === 'essential' || level === 'analytics') listener(level);
  };
  if (typeof window !== 'undefined') {
    window.addEventListener(CONSENT_EVENT, onEvent);
  }
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') {
      window.removeEventListener(CONSENT_EVENT, onEvent);
    }
  };
}
